import type { PricingPlanTier } from '@carsfinders/contracts';

import { t } from '../../lib/i18n';
import type { FinanceClient, FinancePeriod, Invoice, MonthKey, RevenueMonth } from './types';
import {
  formatDayMonth,
  isProvisionalInvoiceNo,
  monthKeyParts,
  overdueDays,
  revenueWindowRange,
} from './utils';

/**
 * Captions for the «Фінанси» screen.
 *
 * This module is the PRESENTATION half of what `utils.ts` computes: everything
 * here reaches into the i18n catalogue, and nothing here does arithmetic. The
 * split is what keeps `utils.ts` testable without a catalogue — a unit test
 * asserting «Вересень» would be testing the translation file, not the code.
 *
 * It exists because the contract carries raw values ONLY: `month: '2026-09'`
 * rather than «Вер», `tier: 'dealer'` rather than «Дилер», `nextDueAt: Date`
 * rather than «через 3 дні». Rendering those is a per-locale job, and the admin
 * `t()` resolves against `uk` while the `en` catalogue is kept in parity by
 * `pnpm check:localization`.
 */

/** «Вересень» / "September" — the full month name for a 1-based month number. */
export function monthName(month: number): string {
  return t(`adminFinance.month.${month}`);
}

/** «Вер» / "Sep" — the three-letter form the chart caps its bars with. */
export function monthShortName(month: number): string {
  return t(`adminFinance.monthShort.${month}`);
}

/**
 * A `YYYY-MM` bucket as the chart's axis label.
 *
 * Falls back to the raw key when it does not parse. A bucket the screen cannot
 * read is a bug worth SEEING — rendering it as month 0 of year NaN would hide
 * the one thing an operator could report.
 */
export function monthKeyShortLabel(key: MonthKey): string {
  const parts = monthKeyParts(key);
  return parts ? monthShortName(parts.month) : key;
}

/** «Вересень 2026» — the same bucket spelled out. */
export function monthKeyLongLabel(key: MonthKey): string {
  const parts = monthKeyParts(key);
  return parts ? `${monthName(parts.month)} ${parts.year}` : key;
}

/**
 * The revenue window, spelled out: «Вересень 2026» / «Квартал» / «2026».
 *
 * Taken from `now` rather than from a catalogue literal. The strings used to be
 * frozen at «Вересень» and «2026», which were correct on the day the artboard
 * was drawn and are a lie on every other day.
 */
export function financePeriodLabel(period: FinancePeriod, now: Date = new Date()): string {
  if (period === 'month') {
    return t('adminFinance.periodLabel.month', {
      month: monthName(now.getMonth() + 1),
      year: now.getFullYear(),
    });
  }
  if (period === 'year') {
    return t('adminFinance.periodLabel.year', { year: now.getFullYear() });
  }
  return t('adminFinance.periodLabel.quarter');
}

/**
 * The revenue card's OWN window label — «Вересень 2026» / «Лип–Гру 2026» —
 * deliberately NOT `financePeriodLabel`.
 *
 * `financePeriodLabel` names the CALENDAR period («2026», the whole year) that
 * `GET /admin/finance/summary?period=` computes over. The revenue card's
 * figure is folded from `months` sliced by `periodMonthIndices`, which is a
 * trailing-window approximation of that same period and can cover fewer
 * months (see `revenueWindowRange` in `utils.ts`). Captioning the card with
 * `financePeriodLabel` would put a calendar-year label over a chart that, in
 * every month but December, charts less than the whole year — read as a bug,
 * or worse, believed. This label instead describes exactly what `indices`
 * selected, so it can never disagree with the total beside it.
 */
export function revenueWindowLabel(months: RevenueMonth[], indices: number[]): string {
  const range = revenueWindowRange(months, indices);
  if (!range) return t('adminFinance.revenue.windowNone');

  if (range.first === range.last) return monthKeyLongLabel(range.first);

  const firstParts = monthKeyParts(range.first);
  const lastParts = monthKeyParts(range.last);
  if (!firstParts || !lastParts) return `${range.first} – ${range.last}`;

  const span = `${monthShortName(firstParts.month)}–${monthShortName(lastParts.month)}`;
  return firstParts.year === lastParts.year
    ? t('adminFinance.revenue.windowRange', { span, year: lastParts.year })
    : `${span} ${firstParts.year}–${lastParts.year}`;
}

/** The shorter form on the period tab strip. */
export function financePeriodTabLabel(period: FinancePeriod, now: Date = new Date()): string {
  if (period === 'month') {
    return t('adminFinance.periodTab.month', { month: monthName(now.getMonth() + 1) });
  }
  if (period === 'year') {
    return t('adminFinance.periodTab.year', { year: now.getFullYear() });
  }
  return t('adminFinance.periodTab.quarter');
}

/** «Роздріб» / «Дилер» / «Дилер Pro» — rendered from `tier`, never sent. */
export function tierLabel(tier: PricingPlanTier): string {
  return t(`adminFinance.tier.${tier}`);
}

/**
 * The invoice number an operator may quote — or the word "provisional".
 *
 * A rollup-derived row's `no` is `PROV-<dealId>`, a mongo id with a prefix. It
 * is a MARKER, not a reference: no such document exists, nothing can be paid
 * against it, and a client handed that string has been given a number nobody
 * can look up. So the marker is rendered as what it means.
 */
export function invoiceNumberLabel(no: string): string {
  return isProvisionalInvoiceNo(no) ? t('adminFinance.table.provisional') : no;
}

/** The deadline phrase under a client's status chip. */
export function clientDueText(client: FinanceClient, now: Date = new Date()): string {
  if (!client.nextDueAt) return t('adminFinance.due.none');
  const late = overdueDays(client.nextDueAt, now);
  if (late > 0) {
    return t('adminFinance.due.overdue', { days: late, date: formatDayMonth(client.nextDueAt) });
  }
  return t('adminFinance.due.by', { date: formatDayMonth(client.nextDueAt) });
}

/**
 * What one invoice is FOR.
 *
 * The fixtures carried a free-text `what` an operator had typed; the contract
 * does not, because nothing on the server stores one. `kind` is what survives,
 * and a provisional row says so beside it rather than pretending to a
 * description it never had.
 */
export function invoiceWhatLabel(invoice: Invoice): string {
  const kind = t(`adminFinance.invoiceKind.${invoice.kind}`);
  return isProvisionalInvoiceNo(invoice.no)
    ? t('adminFinance.table.provisionalWhat', { kind })
    : kind;
}
